import type { ReactElement, ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { createClient } from 'contentful'
import {
  getInvoiceWhatsNewEntries,
  type InvoiceWhatsNewEntryVM,
} from './contentful'
import { INVOICE_WHATSNEW_TEASER_SUPPRESSED_ENTRY_ID } from './constants'
import { InvoiceWhatsNewDetailModal } from './InvoiceWhatsNewDetailModal'
import { InvoiceWhatsNewListModal } from './InvoiceWhatsNewListModal'
import { WhatsNewTeaser } from '@fordeer/ui-components'

export type InvoiceWhatsNewController = {
  enabled: boolean
  openListModal: () => void
  dismissTeaser: () => void
}

const WhatsNewContext = createContext<InvoiceWhatsNewController | null>(null)

const readSuppressedTeaserEntryId = (): string | null => {
  try {
    return localStorage.getItem(INVOICE_WHATSNEW_TEASER_SUPPRESSED_ENTRY_ID)
  } catch {
    return null
  }
}

const writeSuppressedTeaserEntryId = (entryId: string) => {
  try {
    localStorage.setItem(INVOICE_WHATSNEW_TEASER_SUPPRESSED_ENTRY_ID, entryId)
  } catch {
    // ignore quota / private mode
  }
}

export const InvoiceWhatsNewProvider = ({
  children,
}: {
  children: ReactNode
}): ReactElement => {
  const contentfulClient = useMemo(() => {
    const token = import.meta.env.VITE_APP_PUBLIC_CONTENTFUL_DELIVERY_TOKEN as
      | string
      | undefined
    const space = import.meta.env.VITE_APP_PUBLIC_CONTENTFUL_SPACE_ID as string | undefined
    if (!token?.trim() || !space?.trim()) return null

    return createClient({
      accessToken: token,
      space,
      environment:
        (import.meta.env.VITE_APP_PUBLIC_CONTENTFUL_ENVIRONMENT as string | undefined) ??
        'staging',
    })
  }, [])

  const [entries, setEntries] = useState<InvoiceWhatsNewEntryVM[]>([])
  const [loading, setLoading] = useState(Boolean(contentfulClient))
  const [loadError, setLoadError] = useState(false)

  const [suppressedTeaserEntryId, setSuppressedTeaserEntryId] = useState<string | null>(() =>
    readSuppressedTeaserEntryId(),
  )

  const [listModalOpen, setListModalOpen] = useState(false)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [detailEntry, setDetailEntry] = useState<InvoiceWhatsNewEntryVM | null>(null)

  useEffect(() => {
    if (!contentfulClient) {
      setEntries([])
      setLoading(false)
      setLoadError(false)
      return
    }

    let cancelled = false

    const load = async () => {
      try {
        setLoading(true)
        setLoadError(false)

        const nextEntries = await getInvoiceWhatsNewEntries(contentfulClient)
        if (cancelled) return

        setEntries(nextEntries)
      } catch (error) {
        console.error('[Invoice WhatsNew] Failed to fetch Contentful entries', error)
        if (!cancelled) {
          setLoadError(true)
          setEntries([])
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [contentfulClient])

  const latestEntry = entries[0] ?? null

  const suppressTeaserForEntry = useCallback((entryId: string) => {
    writeSuppressedTeaserEntryId(entryId)
    setSuppressedTeaserEntryId(entryId)
  }, [])

  const openDetailModal = useCallback((entry: InvoiceWhatsNewEntryVM) => {
    setDetailEntry(entry)
    setDetailModalOpen(true)
  }, [])

  const handleTeaserOpenDetail = useCallback(() => {
    if (!latestEntry) return
    suppressTeaserForEntry(latestEntry.id)
    openDetailModal(latestEntry)
  }, [latestEntry, openDetailModal, suppressTeaserForEntry])

  const handleTeaserDismiss = useCallback(() => {
    if (!latestEntry) return
    suppressTeaserForEntry(latestEntry.id)
  }, [latestEntry, suppressTeaserForEntry])

  const handleListLearnMore = useCallback(
    (entry: InvoiceWhatsNewEntryVM) => {
      setListModalOpen(false)
      requestAnimationFrame(() => {
        openDetailModal(entry)
      })
    },
    [openDetailModal],
  )

  const handleDetailModalClose = useCallback(() => {
    setDetailModalOpen(false)
  }, [])

  const openListModal = useCallback(() => {
    setListModalOpen(true)
  }, [])

  const handleListModalClose = useCallback(() => {
    setListModalOpen(false)
  }, [])

  const contextValue = useMemo<InvoiceWhatsNewController>(
    () => ({
      enabled: Boolean(contentfulClient),
      openListModal,
      dismissTeaser: handleTeaserDismiss,
    }),
    [contentfulClient, handleTeaserDismiss, openListModal],
  )

  const showTeaser =
    Boolean(latestEntry) &&
    !loading &&
    !loadError &&
    suppressedTeaserEntryId !== latestEntry?.id

  return (
    <WhatsNewContext.Provider value={contextValue}>
      {children}
      {latestEntry ? (
        <WhatsNewTeaser
          entry={latestEntry}
          visible={showTeaser}
          titleId="invoice-whats-new-teaser-title"
          onOpenDetail={handleTeaserOpenDetail}
          onDismiss={handleTeaserDismiss}
        />
      ) : null}
      <InvoiceWhatsNewListModal
        open={listModalOpen}
        onClose={handleListModalClose}
        entries={entries}
        loading={loading}
        loadError={loadError}
        onLearnMore={handleListLearnMore}
      />
      <InvoiceWhatsNewDetailModal
        open={detailModalOpen}
        onClose={handleDetailModalClose}
        entry={detailEntry}
      />
    </WhatsNewContext.Provider>
  )
}

export const useInvoiceWhatsNewController = (): InvoiceWhatsNewController | null =>
  useContext(WhatsNewContext)
